import { Paper, TableContainer } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import * as api from '../../api/app/emailReportAssetApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { DataTable } from '../../components/core/DataTable/DataTable';
import { HeaderCell } from '../../components/core/DataTable/dataTable.types';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { EmailReportAsset } from '../../types/app/emailReportAsset.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
interface Props {
  isReadOnly: boolean;
}
export function EmailReportAssetList({ isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const parentId = Number(id);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const { data, isLoading, isError } = useQuery<EmailReportAsset[]>(['emailReportsAsset', id], () =>
    api.getEmailReportAssetList(Number(id)),
  );

  const { mutate: mutateDeleteEmailReport } = useMutation(api.deleteEmailReportAsset);

  const headerCells: HeaderCell[] = [{ title: 'Asset', key: 'assetName' }];

  const handleOnCreate = () =>
    navigate(`${paths.EMAIL_REPORT_LIST}/${parentId}${paths.EMAIL_REPORT_ASSET_CREATE}`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteEmailReport(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(dictionary.EMAIL_REPORT_DELETED, { variant: 'success' });
        queryClient.invalidateQueries(['emailReportsAsset']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;
  return (
    <>
      <TableContainer component={Paper}>
        <DataTable
          headerCells={headerCells}
          items={data}
          onDeleteClick={handleOnDelete}
          isReadOnly={isReadOnly}
          onCreateClick={handleOnCreate}
        />
      </TableContainer>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
